import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import {
  LayerDTO,
  LayerTypeDTO,
  PFDocProteinFormulationInfoDTO,
  ProteinFormulationDetailInfoDTO,
  ProteinLayerDTO,
} from 'business/models/Models';
import ProteinFormulationService from 'business/services/ProteinFormulationService';
import { TreeService } from 'business/services/TreeService';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import { getParentPath, getVaryAlongName } from 'utils/Global';
import { TABLE_LAYOUT } from './ExperimentReportPDF';

export async function proteinLayerSubReport(
  content: any[],
  expData: ExperimentDtoClient,
) {
  const proteinLayers = expData.layers.filter((lyr) => lyr.type === LayerTypeDTO.Protein);
  for (const proteinLayer of proteinLayers) {
    const layer = proteinLayer;
    let proteinFormulation;
    let proteinFormulationDetails;
    const pfLayerPF = (layer.layerDetail as ProteinLayerDTO)
      .proteinLayerProteinFormulation[0];
    const pfService: ProteinFormulationService = new ProteinFormulationService();
    const treeService = new TreeService();
    let proteinParents = [];

    const pfInfoPromise = async () => {
      if (pfLayerPF.proteinFormulationId > 0) {
        proteinFormulation = await pfService.getProteinFormulation(
          pfLayerPF.proteinFormulationId,
        );
      }
    };

    const pfPathPromise = async () => {
      if (pfLayerPF.proteinFormulationId > 0) {
        proteinParents = await treeService.getDocumentParentNodes(
          pfLayerPF.proteinFormulationId,
          ETreeNodeTypes.Protein,
        );
      }
    };

    const pfDetailsPromise = async () => {
      if (pfLayerPF.proteinFormulationId > 0) {
        proteinFormulationDetails = await pfService.getProteinFormulationDetailsById(
          pfLayerPF.proteinFormulationId,
        );
      }
    };

    await Promise.all([pfInfoPromise(), pfPathPromise(), pfDetailsPromise()]).then(() => {
      content.push({
        text:
          '\n' +
          layer.name +
          ' - ' +
          (layer.height + 1) +
          ' x ' +
          (layer.width + 1) +
          '\n',
        style: 'largeText',
      });
      const path = getParentPath(proteinParents);
      content.push(
        getProteinLayerInfo(
          layer,
          proteinFormulation && proteinFormulation.pfName,
          proteinFormulation && path + '\\' + proteinFormulation.pfName,
        ),
      );

      if (pfLayerPF.proteinFormulationId > 0) {
        content.push({ text: '\nProtein Formulation Detail\n', style: 'largeText' });
        content.push(getProteinFormulationDetailsTable(proteinFormulation));
        if (proteinFormulationDetails.proteinFormulationIngredient.length > 0) {
          content.push({
            text: '\nProtein Formulation Ingredients\n',
            style: 'largeText',
          });
          content.push(getProteinFormulationIngredientsTable(proteinFormulationDetails));
        }
        if (proteinFormulationDetails.proteinFormulationStandardIngredient.length > 0) {
          content.push({
            text: '\nProtein Formulation Standard Ingredients\n',
            style: 'largeText',
          });
          content.push(
            getProteinFormulationStandardIngredientsTable(proteinFormulationDetails),
          );
        }
      }
    });
  }
}

function getProteinFormulationDetailsTable(protein: PFDocProteinFormulationInfoDTO) {
  return {
    style: 'table',
    table: {
      widths: ['20%', '40%', '20%', '20%'],
      body: [
        [
          { text: 'Protein Formulation', bold: true },
          { text: protein.pfName, colSpan: '3' },
          '',
          '',
        ],
        [
          { text: 'ID', bold: true },
          { text: protein.id },
          { text: 'Protein Lot ID', bold: true },
          { text: protein.proteinLotId },
        ],
        [
          { text: 'Class', bold: true },
          { text: protein.pclass },
          { text: 'Date Prepared', bold: true },
          { text: new Date(protein.datePrepared).toLocaleString('en-US') },
        ],
        [
          { text: 'Volume', bold: true },
          { text: protein.volume },
          { text: 'Storage Temprature', bold: true },
          { text: protein.storageTemperature },
        ],
        [
          { text: 'Extinction Coefficient', bold: true },
          { text: protein.extinctionCoefficient },
          { text: 'Notebook Page Number', bold: true },
          { text: protein.notebookPageNo },
        ],
        [
          { text: 'Liquid Class', bold: true },
          { text: protein.liquidClass, colSpan: 3 },
        ],
        [
          { text: 'Notes', bold: true },
          { text: protein.notes, colSpan: 3 },
        ],
      ],
    },
    layout: TABLE_LAYOUT,
  };
}

function getProteinFormulationIngredientsTable(protein: ProteinFormulationDetailInfoDTO) {
  const body = [];
  body.push([
    { text: 'Type', bold: true },
    { text: 'Name', bold: true },
    { text: 'ID', bold: true },
    { text: 'Lot', bold: true },
    { text: 'MW(kDa)', bold: true },
    { text: 'Conc.(mg/mL)', bold: true },
    { text: 'Conc.(mM)', bold: true },
  ]);

  protein.proteinFormulationIngredient.forEach((ing) => {
    body.push([
      { text: ing.proteinFormulationIngredientType.name },
      { text: ing.name },
      { text: ing.userId },
      { text: ing.lot },
      { text: ing.molecularWeight },
      { text: ing.concentrationmgmL },
      { text: ing.concentrationmM },
    ]);
  });

  return {
    style: 'table',
    table: {
      widths: ['10%', '35%', '5%', '5%', '15%', '15%', '15%'],
      body: body,
    },
    layout: TABLE_LAYOUT,
  };
}

function getProteinFormulationStandardIngredientsTable(
  protein: ProteinFormulationDetailInfoDTO,
) {
  const body = [];
  body.push([
    { text: 'Type', bold: true },
    { text: 'Name', bold: true },
    { text: 'Stock Conc.', bold: true },
    { text: 'Conc. Units', bold: true },
    { text: 'Stock Low pH', bold: true },
    { text: 'Stock High pH', bold: true },
    { text: 'Conc.', bold: true },
    { text: 'pH', bold: true },
  ]);

  protein.proteinFormulationStandardIngredient.forEach((ing) => {
    body.push([
      { text: ing.ingredientIngredientType.ingredientType.name },
      { text: ing.ingredientStock.ingredient.longName },
      { text: ing.ingredientStock.stockConcentration },
      { text: ing.ingredientStock.concentrationUnits },
      { text: ing.ingredientStock ? ing.ingredientStock.stockPh : null },
      { text: ing.highPhingredientStock ? ing.highPhingredientStock.stockPh : null },
      { text: ing.concentration },
      { text: ing.ph },
    ]);
  });

  return {
    style: 'table',
    table: {
      widths: ['10%', '30%', '13%', '13%', '12%', '12%', '5%', '5%'],
      body: body,
    },
    layout: TABLE_LAYOUT,
  };
}

function getProteinLayerInfo(layer: LayerDTO, pfname: string, path: string) {
  const lyrDetail = layer.layerDetail as ProteinLayerDTO;

  if (
    lyrDetail.proteinLayerProteinFormulation &&
    lyrDetail.proteinLayerProteinFormulation.length <= 0
  ) {
    return '';
  }

  const pfLayerPf = lyrDetail.proteinLayerProteinFormulation[0];

  let varyAlongName = getVaryAlongName(pfLayerPf.proteinDropVolumeVaryAlong);
  let proteinVaryAlong = '';
  if (varyAlongName == 'Custom') {
    proteinVaryAlong = varyAlongName;
  } else if (varyAlongName == 'None') {
    proteinVaryAlong = varyAlongName + ':  ' + pfLayerPf.startProteinDropVolume + 'µl';
  } else {
    proteinVaryAlong =
      varyAlongName +
      ':  ' +
      pfLayerPf.startProteinDropVolume +
      'µl - ' +
      pfLayerPf.stopProteinDropVolume +
      'µl';
  }

  let wellVaryAlong = '';
  varyAlongName = getVaryAlongName(pfLayerPf.wellDropVolumeVaryAlong);
  if (varyAlongName == 'Custom') {
    wellVaryAlong = varyAlongName;
  } else if (varyAlongName == 'None') {
    wellVaryAlong = varyAlongName + ':  ' + pfLayerPf.startProteinDropVolume + 'µl';
  } else {
    wellVaryAlong =
      varyAlongName +
      ':  ' +
      pfLayerPf.startProteinDropVolume +
      'µl - ' +
      pfLayerPf.stopProteinDropVolume +
      'µl';
  }

  return {
    style: 'table',
    table: {
      widths: ['30%', '70%'],
      body: [
        [{ text: 'Protein Formulation Path', bold: true }, { text: path }],
        [{ text: 'Protein Formulation', bold: true }, { text: pfname }],
        [{ text: 'Well Drop Volume Vary Along', bold: true }, { text: wellVaryAlong }],
        [
          { text: 'Protein Drop Volume Vary Along', bold: true },
          { text: proteinVaryAlong },
        ],
        [{ text: 'Target Drop Number', bold: true }, { text: layer.targetDrop }],
      ],
    },
    layout: TABLE_LAYOUT,
  };
}
